import { useEffect, type RefObject } from 'react';

/** Slow enough to read card copy without motion blur (~14px/s at 60fps) */
const PIXELS_PER_SECOND = 60;

export function usePaymentSceneMarquee(marqueeRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const track = marquee.querySelector<HTMLElement>('[data-marquee-track]:not([aria-hidden="true"])');
    if (!track) return;

    let offset = 0;
    let trackWidth = track.getBoundingClientRect().width;
    let lastTime = performance.now();
    let paused = false;
    let frame = 0;

    const measure = () => {
      trackWidth = track.getBoundingClientRect().width;
      if (offset >= trackWidth && trackWidth > 0) offset %= trackWidth;
    };

    const observer = new ResizeObserver(measure);
    observer.observe(track);

    const onEnter = () => {
      paused = true;
    };

    const onLeave = () => {
      paused = false;
      lastTime = performance.now();
    };

    const tick = (now: number) => {
      const delta = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      if (!paused && trackWidth > 0) {
        offset += PIXELS_PER_SECOND * delta;
        if (offset >= trackWidth) offset -= trackWidth;
        marquee.style.transform = `translate3d(${-offset.toFixed(2)}px, 0, 0)`;
      }

      frame = requestAnimationFrame(tick);
    };

    measure();
    marquee.addEventListener('mouseenter', onEnter);
    marquee.addEventListener('mouseleave', onLeave);
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      marquee.removeEventListener('mouseenter', onEnter);
      marquee.removeEventListener('mouseleave', onLeave);
      marquee.style.transform = '';
    };
  }, [marqueeRef]);
}
